import { useEffect, useRef, useState } from "react";
import { Play, Eye, ArrowRight } from "lucide-react";
import { mediaUrl } from "@/mediaUrl";

/* ──────────────────────────────────────────────────────────────────────────
 * HeroRotator
 *
 * Cinematic hero on the public homepage. Auto-rotates through up to N
 * (default 3) hero stories with a 6-second muted preview clip each, soft
 * cross-fade between, layered text overlay (category pill · views · title),
 * and a CTA button that jumps to the story page.
 *
 *  • Each video uses `preload="metadata"` to keep first-paint fast — only
 *    the active clip's frames are downloaded.
 *  • Inactive clips are paused to save battery on mobile.
 *  • Respects `prefers-reduced-motion`: when set, the rotator becomes a
 *    static still of slide 1 with the dots showing all options as
 *    clickable.
 *  • Clicking the card or the CTA navigates to the story.
 *  • Dot pills at the bottom let viewers jump between slides manually
 *    (resetting the auto-advance timer).
 * ────────────────────────────────────────────────────────────────────── */
const SLIDE_MS = 7000;

export default function HeroRotator({ stories, t }) {
  const items = (stories || []).slice(0, 8);
  const [activeIdx, setActiveIdx] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoRefs = useRef({});
  const timerRef = useRef(null);

  // Track the OS-level reduce-motion preference. We don't reload state
  // on change because hero rotation is benign — but we do skip the
  // auto-advance loop and the cross-fade transitions when set.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Auto-advance loop. Skipped when there's only 1 slide, or when
  // reduce-motion is set.
  useEffect(() => {
    if (items.length <= 1 || reducedMotion) return undefined;
    timerRef.current = setTimeout(() => {
      setActiveIdx((i) => (i + 1) % items.length);
    }, SLIDE_MS);
    return () => clearTimeout(timerRef.current);
  }, [activeIdx, items.length, reducedMotion]);

  // Play/pause videos as the active slide changes.
  useEffect(() => {
    items.forEach((s, idx) => {
      const v = videoRefs.current[s.id];
      if (!v) return;
      if (idx === activeIdx && !reducedMotion) {
        v.muted = true;
        v.currentTime = 0;
        // Some browsers reject programmatic play on first navigation;
        // we tolerate the failure — the still poster stays visible.
        v.play().catch(() => { /* ignored */ });
      } else {
        v.pause();
      }
    });
  }, [activeIdx, items, reducedMotion]);

  if (items.length === 0) return null;

  return (
    <section
      className="px-6 md:px-10 pt-8 md:pt-10"
      data-testid="hero-rotator"
    >
      <div className="max-w-[1200px] mx-auto">
        <div className="relative aspect-[16/9] sm:aspect-[2.4/1] rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black">
          {items.map((s, idx) => {
            const isActive = idx === activeIdx;
            const previewSrc = mediaUrl(`/api/media/video/${s.id}_${s.language}.mp4`);
            const thumbSrc = mediaUrl(s.thumbnail_url);
            return (
              <a
                key={s.id}
                href={`/s/${s.slug}`}
                className={`absolute inset-0 transition-opacity duration-1000 ease-out ${isActive ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
                data-testid={`hero-slide-${s.slug}`}
                aria-hidden={!isActive}
                aria-label={`Watch: ${s.title}`}
              >
                {/* Still poster always loads first so even slow connections
                    see something immediately. The video layers above. */}
                <img
                  src={thumbSrc}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  loading={idx === 0 ? "eager" : "lazy"}
                />
                <video
                  ref={(el) => { if (el) videoRefs.current[s.id] = el; }}
                  src={previewSrc}
                  poster={thumbSrc}
                  muted
                  loop
                  playsInline
                  preload={idx === 0 ? "metadata" : "none"}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={() => { /* keep poster visible — non-fatal */ }}
                />
                {/* Bottom-up gradient for legibility. Stronger at the
                    bottom so the title block reads even on bright clips. */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.85) 100%)",
                  }}
                />
                {/* Top-left eyebrow strip.  Main category (documentary
                    taxonomy) takes priority — it's the "channel" label
                    that tells the visitor what world this story lives
                    in. Falls back to the impact category (animal_rescue
                    etc.) for legacy stories that haven't been
                    categorised yet. */}
                <div className="absolute top-4 md:top-6 left-4 md:left-6 inline-flex items-center gap-2 flex-wrap max-w-[75%]">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-white/95 bg-black/40 backdrop-blur-sm border border-white/10 rounded-md px-2.5 py-1">
                    {t ? t("hero_eyebrow_real_story") : "A real story"}
                  </span>
                  {s.main_category_label ? (
                    <span
                      className="text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-black bg-[color:var(--accent)]/95 rounded-md px-2.5 py-1 font-semibold shadow-lg"
                      data-testid={`hero-main-category-${s.slug}`}
                    >
                      {s.main_category_label}
                    </span>
                  ) : s.category && s.category !== "general" ? (
                    <span className="text-[10px] tracking-[0.18em] uppercase text-white/95 bg-[color:var(--accent)]/95 text-black rounded-md px-2.5 py-1 font-medium">
                      {s.category.replace(/_/g, " ")}
                    </span>
                  ) : null}
                  {s.country && (
                    <span className="text-[10px] tracking-[0.24em] uppercase text-white/85 bg-black/30 backdrop-blur-sm border border-white/10 rounded-md px-2 py-1">
                      {s.country}{s.year != null ? ` · ${s.year}` : ""}
                    </span>
                  )}
                </div>
                {/* Bottom-left title + meta + CTA. */}
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8 lg:p-10">
                  <div className="max-w-3xl">
                    <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white drop-shadow-lg">
                      {s.title}
                    </h2>
                    <div className="mt-3 md:mt-4 flex items-center gap-3 md:gap-4 text-xs md:text-sm text-white/85">
                      {(s.view_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5" />
                          {(s.view_count || 0).toLocaleString()} {(s.view_count || 0) === 1 ? "view" : "views"}
                        </span>
                      )}
                      {s.organization_name && (
                        <span className="hidden sm:inline opacity-80">
                          · {s.organization_name}
                        </span>
                      )}
                    </div>
                    <span
                      className="mt-4 md:mt-5 inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full bg-[color:var(--accent)] text-black hover:opacity-95 transition-opacity"
                    >
                      <Play className="w-4 h-4" fill="currentColor" />
                      {t ? t("hero_cta_watch") : "Watch the story"}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}

          {/* Dot indicators — only when there's more than one slide. */}
          {items.length > 1 && (
            <div className="absolute bottom-3 right-4 md:bottom-5 md:right-6 z-20 flex items-center gap-1.5">
              {items.map((s, idx) => (
                <button
                  key={`dot-${s.id}`}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (timerRef.current) clearTimeout(timerRef.current);
                    setActiveIdx(idx);
                  }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === activeIdx ? "bg-white w-8" : "bg-white/40 hover:bg-white/70 w-3"}`}
                  data-testid={`hero-dot-${idx}`}
                  aria-label={`Show story ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
